import React, { useState, useCallback, useMemo } from 'react';
import { Layout, List } from 'antd';
import VirtualList from 'rc-virtual-list';
import useAgents from '../hooks/useAgents';
import ConversationListItem from './ConversationListItem';
import ConversationsHeader from './ConversationsHeader';
import ConversationSkeleton from './ConversationSkeleton';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import { Resource } from '../../../Constants/socket';
import useConversationManager from '../hooks/useConversationManager';
import useConversationAutoSelect from '../hooks/useConversationAutoSelect';
import { LAYOUT_HEIGHTS, SKELETON_COUNT } from '../constants/layout';


const ConversationsList = ({ onSelectConversation, selectedId, conversations, setConversations, isMobile }) => {
    const { isDarkMode } = useTheme();
    const [hoveredIndex, setHoveredIndex] = useState(null);

    // Agent selection
    const {
        agents,
        selectedAgent,
        loadingAgents,
        setSelectedAgent,
        fetchAgents
    } = useAgents();

    // Subscribe to the selected agent to receive live updates scoped to it
    useSocketSubscription(Resource.AGENT, selectedAgent ? [selectedAgent] : null, true);

    // Unified conversation management - handles pagination, filtering, search, sorting, and initialization
    const {
        hasNextPage,
        loading,
        handleScroll,
        sortBy,
        sortDirection,
        sortedConversations,
        handleSearchChange,
        handleFiltersChange,
        handleSortChange,
        chipFilters
    } = useConversationManager({
        conversations,
        selectedAgent,
        setConversations,
        onSelectConversation,
    });

    // Auto-select logic
    useConversationAutoSelect({
        isMobile,
        selectedId,
        sortedConversations,
        onSelectConversation,
        loading,
        loadingSearch: loading
    });

    // Memoize the render function to prevent recreation on every render
    const renderConversationItem = useCallback((conversation, index) => {
        if (!conversation) return null;
        return (
            <ConversationListItem
                key={conversation.id || index}
                conversation={conversation}
                selectedId={selectedId}
                onSelectConversation={onSelectConversation}
                index={index}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
            />
        );
    }, [selectedId, onSelectConversation, hoveredIndex]);

    // Memoize styles to prevent unnecessary recalculations
    const layoutStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
        borderInlineEnd: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`,
        borderTop: 'none',
        background: isDarkMode ? '#141414' : '#fff',
        overflow: 'hidden'
    }), [isDarkMode]);

    const containerStyle = useMemo(() => ({
        height: LAYOUT_HEIGHTS.CONVERSATIONS_CONTAINER,
        overflow: 'hidden',
        background: isDarkMode ? '#1f1f1f' : '#fff',
        position: 'relative'
    }), [isDarkMode]);

    const virtualListStyle = useMemo(() => ({
        height: '100%',
        overflow: 'auto',
        padding: '8px 0',
    }), []);

    return (
        <Layout style={layoutStyle}>
            <ConversationsHeader
                agents={agents}
                loading={loading}
                loadingAgents={loadingAgents}
                selectedAgent={selectedAgent}
                onAgentChange={setSelectedAgent}
                onAgentSearch={fetchAgents}
                onSearch={handleSearchChange}
                filters={chipFilters}
                onFiltersChange={handleFiltersChange}
                sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                onSortChange={handleSortChange}
            />

            <div style={containerStyle}>
                {loading && sortedConversations.length === 0 ? (
                    <div style={{ overflow: 'auto', height: '100%' }}>
                        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                            <ConversationSkeleton key={index} />
                        ))}
                    </div>
                ) : (
                    <List style={{ height: '100%' }}>
                        <VirtualList
                            data={sortedConversations}
                            fullHeight
                            style={virtualListStyle}
                            itemHeight={60}
                            itemKey="id"
                            onScroll={handleScroll}
                        >
                            {renderConversationItem}
                        </VirtualList>
                        {loading && hasNextPage && (
                            <div style={{ padding: '8px 0', width: '100%' }}>
                                <ConversationSkeleton />
                            </div>
                        )}
                    </List>
                )}
            </div>
        </Layout>
    );
};

export default ConversationsList;
